/**
 * User payments API client wrapping the backend /users/:userId/payments/*
 * endpoints. Manages Stripe account setup/disconnect, creates Stripe
 * onboarding links (with refresh/return URLs), and fetches paginated lists
 * of received and sent payments.
 * Consumed by the PaymentsPage.
 */
import { fetchPaginatedItems, PaginationQueryParams } from "@/api/paginated";
import { api } from "@/lib/axios";
import { PaginatedResponse } from "@/types/pagination";
import { PaymentBountySubmission } from "@/types/payment";
import { PrivateUser } from "@/types/user";

export interface OnboardingLinkRequest {
    refreshUrl: string;
    returnUrl: string;
}

export interface OnboardingLinkResponse {
    onboardingUrl: string;
}

export const UserPaymentAPI = {
    setupAccounts: async (userId: number) => {
        const { data } = await api.post<{ user: PrivateUser }>(
            `/users/${userId}/payments/setup-accounts`
        );
        return data;
    },
    disconnectAccounts: async (userId: number) => {
        const { data } = await api.post<{ user: PrivateUser }>(
            `/users/${userId}/payments/disconnect-accounts`
        );
        return data;
    },
    createOnboardingLink: async (
        userId: number,
        params: OnboardingLinkRequest
    ): Promise<OnboardingLinkResponse> => {
        const { data } = await api.post(`/users/${userId}/payments/onboarding-link`, params);
        return data;
    },
    getReceivedPayments: async (
        userId: number,
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<PaymentBountySubmission>> => {
        return fetchPaginatedItems<PaymentBountySubmission>(
            `/users/${userId}/payments/received`,
            params
        );
    },
    getSentPayments: async (
        userId: number,
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<PaymentBountySubmission>> => {
        return fetchPaginatedItems<PaymentBountySubmission>(
            `/users/${userId}/payments/sent`,
            params
        );
    },
};
